import {
  MenuV2Response,
  ModifierItemV2Response,
  UpdateMenuListOrders,
} from '../../../types/menu.types';

/**
 * An option as the Edit Modifier Group popup holds it. Options added in the
 * popup have no `id` until the group is saved, so `draftKey` is what the drag
 * and drop list (and React) tell them apart by.
 */
export type DraftModifierOption = ModifierItemV2Response & { draftKey: string };

let draftSeq = 0;

export const toDraftOption = (
  option: ModifierItemV2Response
): DraftModifierOption => ({
  ...option,
  draftKey: option.id ? `option-${option.id}` : `new-${++draftSeq}`,
});

/**
 * The reorder to send once the popup's edits are saved, or null when the
 * saved group already has the popup's order.
 *
 * Saving never moves existing options and appends new ones in the order they
 * were sent, so the saved options whose ids were not in the group before
 * (`previousIds`) are the popup's id-less options, in the popup's order.
 */
export const optionsOrderAfterSave = (
  draft: Pick<ModifierItemV2Response, 'id'>[],
  saved: Pick<ModifierItemV2Response, 'id'>[],
  previousIds: Set<ModifierItemV2Response['id']>
): UpdateMenuListOrders[] | null => {
  const added = saved.filter((o) => !previousIds.has(o.id));
  let next = 0;
  const ids = draft.map((o) => o.id || added[next++]?.id);

  const savedIds = new Set(saved.map((o) => o.id));
  if (
    ids.length !== saved.length ||
    ids.some((id) => !id || !savedIds.has(id))
  ) {
    throw new Error(
      'The options were saved, but not their order. Please reload the menu and sort them again.'
    );
  }

  if (ids.every((id, i) => id === saved[i].id)) return null;
  return toOptionsOrder(ids);
};

/** The reorder body: every option of the group, numbered 1..n. */
export const toOptionsOrder = (
  ids: ModifierItemV2Response['id'][]
): UpdateMenuListOrders[] =>
  ids.map((id, i) => ({ id: Number(id), order: i + 1 }));

/** The groups of `menu` whose options are no longer in `before`'s order. */
export const groupsWithNewOptionsOrder = (
  menu: MenuV2Response,
  before: MenuV2Response
) =>
  menu.modifierGroups.filter((group) => {
    const old = before.modifierGroups.find((g) => g.id === group.id);
    return group.options.some((o, i) => o.id !== old?.options[i]?.id);
  });
